import React, { useState } from 'react';
import { useEvents } from '@/contexts/EventContext';
import { Calendar, MapPin, Users, Plus, X, Check, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';

const EventsSection: React.FC = () => {
  const { events, addEvent, rsvpToEvent } = useEvents();
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', date: '', location: '', description: '', type: 'reunion' as const });

  const upcoming = events.filter(e => !e.isPast && new Date(e.date) >= new Date()).sort((a, b) => a.date.localeCompare(b.date));
  const past = events.filter(e => e.isPast || new Date(e.date) < new Date()).sort((a, b) => b.date.localeCompare(a.date));
  const list = tab === 'upcoming' ? upcoming : past;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.date || !form.location) {
      toast.error('Please fill in all required fields');
      return;
    }
    const isPast = new Date(form.date) < new Date();
    try {
      await addEvent({
        ...form,
        attendees: [],
        rsvps: [],
        isPast,
        image: 'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=800',
      });
      toast.success('Event created successfully!');
      setForm({ title: '', date: '', location: '', description: '', type: 'reunion' });
      setShowCreate(false);
      setTab(isPast ? 'past' : 'upcoming');
    } catch (error) {
      console.error('Failed to create canonical family event', error);
      toast.error('Unable to create the event. Please try again.');
    }
  };

  const handleRsvp = async (eventId: string, status: 'yes' | 'no' | 'maybe') => {
    try {
      await rsvpToEvent(eventId, status);
      toast.success(`RSVP saved: ${status === 'yes' ? 'Going' : status === 'no' ? 'Not going' : 'Maybe'}`);
    } catch (error) {
      console.error('Failed to save canonical RSVP', error);
      toast.error('Unable to save your RSVP. Confirm your family profile is claimed and try again.');
    }
  };

  const typeColors: Record<string, string> = {
    reunion: 'bg-green-100 text-green-800 border-green-200',
    wedding: 'bg-pink-100 text-pink-800 border-pink-200',
    birthday: 'bg-amber-100 text-amber-800 border-amber-200',
    anniversary: 'bg-rose-100 text-rose-800 border-rose-200',
    milestone: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',

    memorial: 'bg-stone-100 text-stone-800 border-stone-200',
  };

  return (
    <section id="events" className="py-20 bg-gradient-to-b from-[#FAF6F0] to-[#F5EDE0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-amber-950 mb-3">Family Events</h2>
            <p className="text-amber-800/80 max-w-2xl">Celebrate past memories and plan future gatherings. Keep the family connected across generations.</p>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-gradient-to-r from-amber-800 to-amber-900 hover:from-amber-900 hover:to-amber-950 text-amber-50 rounded-full shadow-lg self-start md:self-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Post New Event
          </Button>
        </div>

        {/* Tabs */}
        <div className="inline-flex bg-white border border-amber-200 rounded-full p-1 shadow-sm mb-8">
          <button
            onClick={() => setTab('upcoming')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${tab === 'upcoming' ? 'bg-amber-800 text-amber-50' : 'text-amber-900'}`}
          >
            Upcoming ({upcoming.length})
          </button>
          <button
            onClick={() => setTab('past')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${tab === 'past' ? 'bg-amber-800 text-amber-50' : 'text-amber-900'}`}
          >
            Past Memories ({past.length})
          </button>
        </div>

        {/* Events grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map(ev => (
            <div key={ev.id} className="bg-white rounded-2xl overflow-hidden shadow-md border border-amber-100 hover:shadow-xl transition-shadow flex flex-col">
              <div className="relative h-48">
                <img src={ev.image} alt={ev.title} className={`w-full h-full object-cover ${ev.isPast ? 'sepia-[0.2]' : ''}`} />
                <div className="absolute top-3 left-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${typeColors[ev.type]}`}>
                    {ev.type.charAt(0).toUpperCase() + ev.type.slice(1)}
                  </span>
                </div>
                <div className="absolute bottom-3 right-3 bg-white/95 rounded-lg px-3 py-1.5 shadow-md">
                  <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
                    {new Date(ev.date).toLocaleDateString('en-US', { month: 'short' })}
                  </div>
                  <div className="font-serif text-lg font-bold text-amber-950 leading-none">
                    {new Date(ev.date).getDate()}
                  </div>
                </div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-serif text-xl font-bold text-amber-950 mb-2">{ev.title}</h3>
                <div className="space-y-1.5 text-sm text-amber-800/80 mb-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(ev.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5" />
                    {ev.location}
                  </div>
                </div>
                <p className="text-sm text-amber-900/80 mb-4 flex-1">{ev.description}</p>

                {!ev.isPast && (
                  <div className="pt-4 border-t border-amber-100">
                    <div className="text-xs text-amber-700 mb-2 font-medium">
                      {ev.rsvps && ev.rsvps.length > 0 ? (
                        <span><Users className="w-3 h-3 inline mr-1" />{ev.rsvps.filter(r => r.status === 'yes').length} going · {ev.rsvps.filter(r => r.status === 'maybe').length} maybe</span>
                      ) : 'Be the first to RSVP'}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleRsvp(ev.id, 'yes')} className="flex-1 px-3 py-2 bg-green-100 hover:bg-green-200 text-green-800 text-xs font-semibold rounded-lg flex items-center justify-center gap-1 transition-colors">
                        <Check className="w-3 h-3" /> Going
                      </button>
                      <button onClick={() => handleRsvp(ev.id, 'maybe')} className="flex-1 px-3 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold rounded-lg flex items-center justify-center gap-1 transition-colors">
                        <HelpCircle className="w-3 h-3" /> Maybe
                      </button>
                      <button onClick={() => handleRsvp(ev.id, 'no')} className="flex-1 px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-semibold rounded-lg flex items-center justify-center gap-1 transition-colors">
                        <X className="w-3 h-3" /> Can't
                      </button>
                    </div>
                  </div>
                )}
                {ev.isPast && ev.attendees.length > 0 && (
                  <div className="pt-4 border-t border-amber-100 text-xs text-amber-700 flex items-center gap-2">
                    <Users className="w-3.5 h-3.5" />
                    {ev.attendees.length} family members attended
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {list.length === 0 && (
          <div className="text-center py-16 bg-white/60 rounded-2xl border border-amber-200">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-amber-400" />
            <p className="text-amber-700">No {tab} events yet.</p>
            {tab === 'upcoming' && <p className="text-sm text-amber-600 mt-1">Click "Post New Event" to plan your next gathering!</p>}
          </div>
        )}
      </div>

      {/* Create event modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[60] bg-amber-950/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-amber-100">
              <h3 className="font-serif text-2xl font-bold text-amber-950">Post New Event</h3>
              <button onClick={() => setShowCreate(false)} className="p-2 hover:bg-amber-50 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-amber-900 mb-1">Event Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:outline-none"
                  placeholder="Summer Family Reunion"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-amber-900 mb-1">Date *</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={e => setForm({ ...form, date: e.target.value })}
                    className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-amber-900 mb-1">Type</label>
                  <select
                    value={form.type}
                    onChange={e => setForm({ ...form, type: e.target.value as any })}
                    className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:outline-none"
                  >
                    <option value="reunion">Reunion</option>
                    <option value="wedding">Wedding</option>
                    <option value="birthday">Birthday</option>
                    <option value="anniversary">Anniversary</option>
                    <option value="milestone">Milestone</option>
                    <option value="memorial">Memorial</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-900 mb-1">Location *</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={e => setForm({ ...form, location: e.target.value })}
                  className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:outline-none"
                  placeholder="Lake Tahoe, California"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-900 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:outline-none resize-none"
                  placeholder="Tell the family about this event..."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1 bg-amber-800 hover:bg-amber-900 text-amber-50">Create Event</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default EventsSection;
